import {useCallback, useEffect, useMemo, useRef, useState} from "react";

const WHITE_ARROW = "rgba(47, 84, 40, 0.9)";
const BLACK_ARROW = "rgba(214, 102, 132, 0.92)";
const STEP_MS = 1000;
const RING_MS = 2600;

/**
 * Showing one of Rook's lines on a board. While a line is shown the board displays the line's positions instead of
 * the real one, and the player can't move until they close it (Esc or the close button).
 *
 * Returns everything the board and the control bar need:
 *  - active: a line is being shown
 *  - fen / lastMove / annotations: what the board should display (null when showing the real position)
 *  - show(line, key), close(), go(step), next(), prev(), togglePlay(), highlight(square)
 */
const useLinePreview = () => {
    const [state, setState] = useState(null);   // {line, key, step, playing}
    const [ring, setRing] = useState(null);     // a square highlighted from the chat
    const timer = useRef(null);
    const ringTimer = useRef(null);

    const stopTimer = () => {
        clearInterval(timer.current);
        timer.current = null;
    };

    const close = useCallback(() => {
        stopTimer();
        setState(null);
    }, []);

    const go = useCallback((step) => {
        stopTimer();
        setState((s) => (s ? {...s, step: Math.max(-1, Math.min(s.line.moves.length - 1, step)), playing: false} : s));
    }, []);

    const play = useCallback((fromStart) => {
        stopTimer();
        setState((s) => (s ? {...s, step: fromStart || s.step >= s.line.moves.length - 1 ? -1 : s.step, playing: true} : s));
        timer.current = setInterval(() => {
            setState((s) => {
                if (!s) return s;
                if (s.step >= s.line.moves.length - 1) {
                    stopTimer();
                    return {...s, playing: false};
                }
                return {...s, step: s.step + 1};
            });
        }, STEP_MS);
    }, []);

    // Clicking a line plays it from the start; clicking a single move shows the position after it
    const show = useCallback((line, key, step = null) => {
        stopTimer();
        setState({line, key, step: step === null ? -1 : step, playing: false});
        if (step === null) setTimeout(() => play(true), 350);
    }, [play]);

    const togglePlay = useCallback(() => {
        if (timer.current) {
            stopTimer();
            setState((s) => (s ? {...s, playing: false} : s));
        } else play(false);
    }, [play]);

    const highlight = useCallback((square) => {
        clearTimeout(ringTimer.current);
        setRing(square);
        ringTimer.current = setTimeout(() => setRing(null), RING_MS);
    }, []);

    useEffect(() => () => {
        stopTimer();
        clearTimeout(ringTimer.current);
    }, []);

    // Esc closes, the arrow keys step through the line
    useEffect(() => {
        if (!state) return;
        const onKey = (event) => {
            const tag = event.target?.tagName;
            if (tag === "INPUT" || tag === "TEXTAREA") return;
            if (event.key === "Escape") close();
            else if (event.key === "ArrowRight") go(state.step + 1);
            else if (event.key === "ArrowLeft") go(state.step - 1);
            else return;
            event.preventDefault();
            event.stopImmediatePropagation();
        };
        window.addEventListener("keydown", onKey, true);
        return () => window.removeEventListener("keydown", onKey, true);
    }, [state, close, go]);

    const view = useMemo(() => {
        const rings = ring ? [{square: ring}] : [];
        if (!state) return {fen: null, lastMove: null, annotations: rings.length ? {arrows: [], rings} : null};
        const {line, step} = state;
        const current = step >= 0 ? line.moves[step] : null;
        const firstIsWhite = line.startFen.split(" ")[1] === "w";
        const arrows = line.moves.map((m, i) => {
            const white = (i % 2 === 0) === firstIsWhite;
            return {
                from: m.from,
                to: m.to,
                color: white ? WHITE_ARROW : BLACK_ARROW,
                label: String(i + 1),
                opacity: i === step ? 1 : i < step ? 0.25 : 0.5
            };
        });
        return {
            fen: current ? current.fen : line.startFen,
            lastMove: current ? {from: current.from, to: current.to} : null,
            annotations: {arrows, rings}
        };
    }, [state, ring]);

    return {
        active: !!state,
        line: state?.line || null,
        key: state?.key || null,
        step: state ? state.step : -1,
        playing: !!state?.playing,
        ...view,
        show, close, go, togglePlay, highlight,
        next: () => state && go(state.step + 1),
        prev: () => state && go(state.step - 1)
    };
};

export default useLinePreview;
